import type { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  type ChatConversation,
  ChatHistoryContext,
  type ChatHistoryContextState,
  type ChildrenProp,
  usePubSub,
} from "@huub-web/ui";
import { useAPI } from "@huub-web/backend";

type ChatHistoryProviderProps = ChildrenProp & {
  activeConversationID?: string | null;
};

/**
 * ChatHistoryProvider is a provider for the ChatHistoryContext.
 * It provides the chat history and the active conversation ID.
 */
export const ChatHistoryProvider: FC<ChatHistoryProviderProps> = ({
  activeConversationID,
  children,
}) => {
  const { $get } = useAPI();
  const pubSub = usePubSub();
  const { t } = useTranslation();

  const fetchConversationHistory: ChatHistoryContextState["fetchConversationHistory"] =
    async () => {
      try {
        const response = await $get("/conversations/");
        const data = response.data as ChatConversation[];

        // TODO: Have API return current paginated overview
        const overview = data.map((chat) => ({
          id: chat.id,
          thread_id: chat.thread_id,
          title: chat.title,
          created: chat.created,
        }));

        return {
          data: overview,
          total: data.length,
          page: 1,
        };
      } catch (error) {
        console.error("Error fetching chat history:", error);
        pubSub.publish("snackbar", {
          body: t("general.error"),
          variant: "error",
        });

        return {
          data: [],
          total: 0,
          page: 1,
        };
      }
    };

  const fetchConversation: ChatHistoryContextState["fetchConversation"] =
    async (id) => {
      try {
        const response = await $get(`/conversations/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching chat history:", error);
        pubSub.publish("snackbar", {
          body: t("general.error"),
          variant: "error",
        });

        return [];
      }
    };

  return (
    <ChatHistoryContext.Provider
      value={{
        fetchConversationHistory,
        fetchConversation,
        activeConversationID: activeConversationID ?? null,
      }}
    >
      {children}
    </ChatHistoryContext.Provider>
  );
};
